import { ChevronDown, Clipboard } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { Badge, Group, Menu, Pagination, Text, Tooltip } from "@mantine/core";
import { Navigate, useSearchParams } from "react-router-dom";
import { useClipboard } from "@mantine/hooks";
import { dateFormat } from "../../../utils/DateTime";
import AnnouncementTableMenu from "./AnnouncementTableMenu";
import Announcement from "../services/model/Announcement";
import { badgeConfig } from "../../../utils/Theme";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import AnnouncementTableEmptyStateContainer from "./AnnouncementTableEmptyStateContainer";
import useAnnouncementListApiService from "../services/api/AnnouncementListApiService";
import { AuthContext } from "../../../providers/AuthProvider";
import { reduceText } from "../../../utils/Text";
import DownloadAnnouncementAttachmentActionIcon from "../elements/DownloadAnnouncementAttachmentActionIcon";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type TargetingMenuProps = {
  label: string;
  items: { id: number; name: string }[];
  emptyText: string;
};

const TargetingMenu: React.FC<TargetingMenuProps> = ({
  label,
  items,
  emptyText,
}) => {
  if (items && items.length > 0) {
    return (
      <Menu
        shadow="md"
        position="bottom"
        control={
          <button
            type="button"
            style={{
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              cursor: "pointer",
              padding: "8px 12px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "120px",
              color: "#495057",
            }}
          >
            <span>View all</span>
            <ChevronDown size={16} />
          </button>
        }
        styles={{
          body: { padding: "8px 0", width: "220px" },
          label: {
            fontSize: "14px",
            fontWeight: 600,
            color: "#495057",
            padding: "8px 16px 4px",
          },
          item: { fontSize: "14px", padding: "10px 16px", color: "#495057" },
        }}
      >
        <Menu.Label>{label}</Menu.Label>
        {items.map((item) => (
          <Menu.Item key={item.id}>{item.name}</Menu.Item>
        ))}
      </Menu>
    );
  }
  return <Text size="xs">{emptyText}</Text>;
};

type AnnouncementTableProps = {
  selection: Announcement[];
  onSelection: React.Dispatch<React.SetStateAction<Announcement[]>>;
};

const AnnouncementTable: React.FC<AnnouncementTableProps> = ({
  selection,
  onSelection,
}) => {
  const [activePage, setPage] = useState(1);
  const [, setSearchParams] = useSearchParams(
    new URLSearchParams(`page=${activePage}`)
  );
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();
  const tableMaxHeight = ResponsiveHeight(
    "",
    "80vh",
    "80vh",
    "80vh",
    "75vh",
    "80vh"
  );

  const clipboard = useClipboard({ timeout: 500 });

  useEffect(() => {
    setSearchParams(new URLSearchParams(`page=${activePage}`));
  }, [activePage]);

  useEffect(() => {
    if (clipboard.copied) {
      showToast({ strapiId: "announcement_copied" });
    }
  }, [clipboard.copied]);

  const { isLoading, isError, error, data } = useAnnouncementListApiService({
    page: activePage,
    employer_id: auth?.employer.id || 0,
    pagination: true,
  });

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!data) {
    return <AnnouncementTableEmptyStateContainer />;
  }

  if (data.results && !data.results.length) {
    return <AnnouncementTableEmptyStateContainer />;
  }

  return (
    <>
      <DirectoryTable<Announcement>
        tableMaxHeight={tableMaxHeight}
        data={data.results}
        selection={selection}
        onSelect={onSelection}
        headers={[
          "Content",
          "Views",
          "Launch Date",
          "Close Date",
          "Tags/Segments",
          "Attachments",
          "Status",
          "",
        ]}
        isSelectable={(item) => !item.is_multi_company}
        rows={data.results.map((it) => ({
          content: (
            <Tooltip
              radius="md"
              position="bottom"
              placement="center"
              label={it.content}
              styles={{
                body: {
                  backgroundColor: "#ffffff",
                  color: "#000000",
                },
              }}
              wrapLines
              width={300}
              withArrow
              openDelay={300}
            >
              <Group>
                <Clipboard
                  onClick={() => clipboard.copy(it.content)}
                  size={18}
                />
                <div>
                  <Text onClick={() => clipboard.copy(it.content)} size="sm">
                    {reduceText(it.title ? it.title : it.content, 30)}
                  </Text>
                  <div className="pt-1 text-xs">
                    {it.is_multi_company ? (
                      <>
                        <span className="bg-pink-50 py-[1px] px-1">
                          Multi-company
                        </span>
                        {it.sent_by_external_company && (
                          <p className="text-greyscale-text-caption mt-0.5">
                            via {it.sent_by_external_company.company_name}
                          </p>
                        )}
                      </>
                    ) : (
                      <span className="bg-green-50 py-[1px] px-1">
                        {it.announcement_type.name}
                      </span>
                    )}
                  </div>
                </div>
              </Group>
            </Tooltip>
          ),
          stats: `${it.announcement_stats.announcements_read_count}/${
            it.audience_size ?? "-"
          }`,
          scheduled_start_date: it.scheduled_start_date
            ? dateFormat(it.scheduled_start_date)
            : "-",
          scheduled_end_date: it.scheduled_end_date
            ? dateFormat(it.scheduled_end_date)
            : "-",
          tags_segments: it.is_multi_company ? (
            <Text size="xs">{"\u2014"}</Text>
          ) : (
            <div className="flex flex-col gap-2">
              {it.targeting_type === "segments" ? (
                <TargetingMenu
                  label="Segments"
                  items={it.segments || []}
                  emptyText="-"
                />
              ) : (
                <TargetingMenu
                  label="Tags"
                  items={it.tags || []}
                  emptyText="-"
                />
              )}
            </div>
          ),
          attachment: it.media_url ? (
            <DownloadAnnouncementAttachmentActionIcon fileUrl={it.media_url} />
          ) : (
            "-"
          ),
          status: (
            <Badge
              style={{
                color: badgeConfig[it.announcement_state || ""],
                backgroundColor: badgeConfig[it.announcement_state || ""],
              }}
            >
              {it.announcement_state || ""}
            </Badge>
          ),
          actions: it.is_multi_company ? (
            <Text size="xs">{"\u2014"}</Text>
          ) : (
            <AnnouncementTableMenu
              id={it.id}
              status={it.announcement_state || ""}
            />
          ),
        }))}
      />
      {data.results.length > 0 && (
        <Pagination
          position="right"
          mt={16}
          onChange={setPage}
          total={data.pages}
          initialPage={1}
          page={activePage}
        />
      )}
    </>
  );
};

export default AnnouncementTable;
